/**
 * Módulo de órdenes de trabajo — CRUD, actividades, evidencias con galería, programación y estadísticas
 */

import { createCrudMethods } from '../base/crudMethods';
import { downloadBlobFromEndpoint } from '../../utils/downloadHelpers';

/**
 * Crea el módulo de órdenes de trabajo
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @param {Function} deps.authenticatedFormDataRequest - Función para uploads y descargas
 * @returns {Object} Métodos del módulo órdenes de trabajo
 */
export function createOrdenesTrabajoModule({ authenticatedRequest, authenticatedFormDataRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/ordenes-trabajo',
        entityName: 'ordenTrabajo',
        entityNamePlural: 'ordenesTrabajo'
      },
      { authenticatedRequest }
    ),

    /**
     * Inicia una OT programada asignada al usuario autenticado
     *
     * @param {number} id - ID de la orden de trabajo
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async iniciarOrdenTrabajo(id) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${id}/iniciar`, {
        method: 'PUT'
      });
    },

    /**
     * Obtiene órdenes de trabajo asignadas al usuario autenticado
     *
     * @param {Object} [params] - Parámetros de consulta (search, estado, prioridad, page, limit)
     * @returns {Promise<{success: boolean, data: Array, pagination: Object}>}
     */
    async getMisOrdenesTrabajo(params = {}) {
      const qs = new URLSearchParams(params).toString();
      return await authenticatedRequest(qs ? `/api/ordenes-trabajo/mis-ordenes?${qs}` : '/api/ordenes-trabajo/mis-ordenes');
    },

    /**
     * Obtiene órdenes de trabajo próximas (7 días)
     *
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getOrdenTrabajoProximas() {
      return await authenticatedRequest('/api/ordenes-trabajo/proximos');
    },

    /**
     * Obtiene estadísticas de órdenes de trabajo para dashboard
     *
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async getOrdenTrabajoStats() {
      return await authenticatedRequest('/api/ordenes-trabajo/stats/resumen');
    },

    /**
     * Obtiene lista de tipos de mantenimiento para selects
     *
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getTiposOrdenTrabajo() {
      return await authenticatedRequest('/api/ordenes-trabajo/tipos/lista');
    },

    /**
     * Obtiene actividades de una orden de trabajo con conteo de evidencia
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getActividadesOrdenTrabajo(ordenTrabajoId) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/actividades`);
    },

    /**
     * Crea una actividad ad-hoc en una orden de trabajo
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {Object} data - { nombre, descripcion }
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async createActividadOrdenTrabajo(ordenTrabajoId, data) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/actividades`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Cambia el estado de una actividad (en_progreso, completado, omitido)
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {number} actividadId - ID de la actividad
     * @param {Object} data - { estado: 'en_progreso'|'completado'|'omitido', observaciones? }
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async cambiarEstadoActividad(ordenTrabajoId, actividadId, data) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/actividades/${actividadId}/estado`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Elimina una actividad de orden de trabajo (soft delete)
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {number} actividadId - ID de la actividad
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteActividadOrdenTrabajo(ordenTrabajoId, actividadId) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/actividades/${actividadId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Copia actividades de una plantilla a una orden de trabajo
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {number} plantillaId - ID de la plantilla
     * @returns {Promise<{success: boolean, data: {actividades_copiadas: number}}>}
     */
    async copiarPlantillaAOrdenTrabajo(ordenTrabajoId, plantillaId) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/copiar-plantilla`, {
        method: 'POST',
        body: JSON.stringify({ plantilla_mantenimiento_id: plantillaId })
      });
    },

    /**
     * Sube evidencia (foto/video) para una actividad
     *
     * @param {number} actividadId - ID de la actividad
     * @param {FormData} formData - Debe contener: archivo, tipo ('antes'|'despues'), descripcion (opcional)
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async uploadEvidencia(actividadId, formData) {
      return authenticatedFormDataRequest(`/api/evidencia-actividad/actividad/${actividadId}`, {
        method: 'POST',
        body: formData
      });
    },

    /**
     * Obtiene evidencias de una actividad
     *
     * @param {number} actividadId - ID de la actividad
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getEvidenciasActividad(actividadId) {
      return await authenticatedRequest(`/api/evidencia-actividad/actividad/${actividadId}`);
    },

    /**
     * Obtiene todas las evidencias de una orden de trabajo (galeria)
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getEvidenciasOrdenTrabajo(ordenTrabajoId) {
      return await authenticatedRequest(`/api/evidencia-actividad/orden-trabajo/${ordenTrabajoId}`);
    },

    /**
     * Elimina una evidencia (soft delete)
     *
     * @param {number} evidenciaId - ID de la evidencia
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteEvidencia(evidenciaId) {
      return await authenticatedRequest(`/api/evidencia-actividad/${evidenciaId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Actualiza la descripción de una evidencia
     *
     * @param {number} evidenciaId - ID de la evidencia
     * @param {string} descripcion - Nueva descripción (vacío para limpiar)
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async updateEvidenciaDescripcion(evidenciaId, descripcion) {
      return await authenticatedRequest(`/api/evidencia-actividad/${evidenciaId}`, {
        method: 'PATCH',
        body: JSON.stringify({ descripcion })
      });
    },

    /**
     * Descarga un archivo de evidencia
     *
     * Retorna la Response cruda para que el caller extraiga el blob directamente.
     *
     * @param {number} evidenciaId - ID de la evidencia
     * @returns {Promise<Response>}
     */
    async downloadEvidencia(evidenciaId) {
      return await authenticatedFormDataRequest(`/api/evidencia-actividad/${evidenciaId}/download`, {
        responseType: 'blob'
      });
    },

    /**
     * Programa (o reprograma) una orden de trabajo a una nueva fecha de ejecución
     *
     * @param {number} id - ID de la orden de trabajo
     * @param {Object} data - { fecha_ejecucion: string, motivo_reprogramacion_id?: number, motivo?: string }
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async programarOrdenTrabajo(id, data) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${id}/programar`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Obtiene historial de programación de una orden de trabajo
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getHistorialProgramacion(ordenTrabajoId) {
      return await authenticatedRequest(`/api/ordenes-trabajo/${ordenTrabajoId}/historial-programacion`);
    },

    /**
     * Genera y firma el informe de una orden de trabajo completada
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {Object} [data]         - { observaciones?: string }
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async generarInformeOrdenTrabajo(ordenTrabajoId, data = {}) {
      return await authenticatedRequest(
        `/api/ordenes-trabajo/${ordenTrabajoId}/informe`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /**
     * Descarga el informe PDF firmado de una orden de trabajo
     *
     * Usa authenticatedFormDataRequest vía downloadBlobFromEndpoint para
     * conservar acceso a headers (Content-Disposition con el nombre del archivo).
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @returns {Promise<{blob: Blob, filename: string}>}
     */
    async descargarInformeOrdenTrabajo(ordenTrabajoId) {
      return downloadBlobFromEndpoint(
        authenticatedFormDataRequest,
        `/api/ordenes-trabajo/${ordenTrabajoId}/informe`,
        `informe-ot-${ordenTrabajoId}.pdf`
      );
    },

    /**
     * Envía el informe de una orden de trabajo por email
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {string} emailDestino   - Dirección de email destinataria
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async enviarInformeOrdenTrabajo(ordenTrabajoId, emailDestino) {
      return await authenticatedRequest(
        `/api/ordenes-trabajo/${ordenTrabajoId}/informe/enviar-email`,
        { method: 'POST', body: JSON.stringify({ email_destino: emailDestino }) }
      );
    },

    /**
     * Adjunta una foto de la galería personal como evidencia de una actividad
     *
     * @param {number} actividadId - ID de la actividad
     * @param {Object} data
     * @param {number} data.foto_galeria_id - ID de la foto en galería
     * @param {string} data.tipo - 'antes' o 'despues'
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    /**
     * Termina formalmente una OT y genera informe PDF firmado
     *
     * @param {number} ordenTrabajoId - ID de la orden de trabajo
     * @param {Object} [data] - { observaciones_cierre?: string }
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async terminarOrdenTrabajo(ordenTrabajoId, data = {}) {
      return await authenticatedRequest(
        `/api/ordenes-trabajo/${ordenTrabajoId}/terminar`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async adjuntarFotoComoEvidencia(actividadId, data) {
      return await authenticatedRequest(
        `/api/evidencia-actividad/actividad/${actividadId}/desde-galeria`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    }
  };
}
